import { cache } from 'react';
import { clsx } from 'clsx';
import { ISbStoryData } from 'storyblok-js-client';

import { getStories } from '@monoimin/storyblok/getStories';
import {
  C2ACommonStoryblok,
  C2AFactsheetStoryblok,
  C2AMeetingStoryblok,
  C2APopupStoryblok,
  ExpertStoryblok,
  InvestmentProgramStoryblok,
  Locales,
} from '@monoimin/sb-types';
import {
  INVESTMENT_PROGRAM_COMPARISON_FIELDS,
  locales,
} from '@monoimin/constants';

export const getC2AContainerClasses = (
  isRichText: boolean | undefined,
  isInsideContainer?: boolean
) =>
  clsx({
    'md:container': !isRichText && !isInsideContainer,
    'w-[calc(100%+40px)] mx-[-20px] lg:mx-auto lg:relative lg:w-[150%]':
      isRichText && !isInsideContainer,
  });

export const getBlokVariant = (
  content: C2ACommonStoryblok | C2AFactsheetStoryblok | C2AMeetingStoryblok
) =>
  content?.variant ||
  content?.meetingVariant ||
  content?.factsheetVariant ||
  content?.priceVariant ||
  content?.subscribeVariant;

export type C2aBlockDataDetails = {
  slug?: string;
  name?: string;
  expertStory?: null | ISbStoryData<ExpertStoryblok>;
  investmentProgramStory?: null | ISbStoryData<InvestmentProgramStoryblok>;
};

const _getInvestmentProgramData = async (uuid: string) => {
  if (!uuid) return null;

  const {
    data: { stories },
  } = await getStories<InvestmentProgramStoryblok>(
    {
      by_uuids: uuid,
      excluding_fields: INVESTMENT_PROGRAM_COMPARISON_FIELDS,
    },
    {
      next: {
        revalidate: 10_800, // 3 hours
        tags: [`investment-program-${uuid}`],
      },
    }
  );

  return stories?.[0] || null;
};

const getInvestmentProgramData = cache(_getInvestmentProgramData);

const _getC2aBlockData = async (params: { locale: Locales; uuid: string }) => {
  const { story } = await getC2aStory<
    C2AFactsheetStoryblok | C2AMeetingStoryblok | C2ACommonStoryblok
  >(params);

  const details: C2aBlockDataDetails = {
    slug: story?.slug,
    name: story?.name,
    expertStory: null,
  };

  if (story?.content?.expert) {
    const {
      data: { stories },
    } = await getStories<ExpertStoryblok>(
      {
        by_uuids: story.content.expert,
        per_page: 1,
        page: 1,
      },
      {
        next: {
          revalidate: 10_800, // 3 hours
          tags: [`c2a-block-expert-${story.content.expert}`],
        },
      }
    );
    details.expertStory = stories?.[0] || null;
  }

  const investmentProgram = story?.content?.investmentProgram;
  if (investmentProgram && typeof investmentProgram === 'string') {
    details.investmentProgramStory = await getInvestmentProgramData(
      investmentProgram
    );
  }

  return {
    details,
    story,
  };
};

const _getC2aBlocksData = async (params: {
  locale: Locales;
  content_type: string;
}) => {
  const { stories } = await getC2aStories<C2AFactsheetStoryblok>(params);

  return {
    stories,
  };
};

export const getC2aBlocksData = cache(_getC2aBlocksData);
export const getC2aBlockData = cache(_getC2aBlockData);

const getC2aStory = cache(async function <T>({
  locale,
  uuid,
}: {
  locale: Locales;
  uuid: string;
}) {
  if (!uuid) {
    return { story: null };
  }
  const {
    data: { stories },
  } = await getStories(
    {
      language: locale,
      by_uuids: uuid,
    },
    {
      next: {
        revalidate: 10_800, // 3 hours
        tags: [`c2a-story-${uuid}-${locale}`],
      },
    }
  );

  return { story: stories?.[0] as ISbStoryData<T> };
});

export const getC2aStories = cache(async function <T>({
  locale,
  content_type,
}: {
  locale: Locales;
  content_type: string;
}) {
  if (!content_type) {
    return { stories: [] };
  }

  const {
    data: { stories },
  } = await getStories<T>(
    {
      language: locale,
      content_type,
    },
    {
      next: {
        revalidate: 10_800, // 3 hours
        tags: [`c2a-stories-${content_type}-${locale}`],
      },
    }
  );

  return { stories };
});

const capitalizeFirst = (str: string) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const getAnalyticsBlockName = (
  content:
    | C2AFactsheetStoryblok
    | C2AMeetingStoryblok
    | C2ACommonStoryblok
    | C2APopupStoryblok,
  investmentProgramStory?: ISbStoryData<InvestmentProgramStoryblok> | null,
  expertStory?: null | ISbStoryData<ExpertStoryblok>,
  customBlockType?: string
) => {
  if (content?.component === 'c2aCommon') {
    return `CTA ${capitalizeFirst(content.variant || 'Unresolved')} ${
      content.title
    }`;
  }

  if (content?.component === 'c2aMeeting') {
    if (content.meetingVariant === 'contacts') {
      return 'CTA Meeting Contacts Page';
    }

    return `CTA Meeting ${
      slugToName(expertStory?.slug) || expertStory?.name || 'Unresolved expert'
    }`;
  }

  if (
    content?.component === 'c2aFactsheet' &&
    customBlockType?.toLocaleLowerCase() === 'guide'
  ) {
    return 'CTA Guide Light Travel the world without visas';
  }

  const blockType = content?.blockType || 'Unresolved block type';

  const variantTechName = content.factsheetVariant || content.meetingVariant;
  const variant = variantTechName
    ? capitalizeFirst(variantTechName.replace('price', '')).trim()
    : '';

  const investmentProgramName = investmentProgramStory?.name;
  const finalBlockName = investmentProgramName || content.customBlockName;

  const result = `CTA ${blockType} ${
    variant ? variant + ' ' : ''
  }${finalBlockName}`;

  return result.trim();
};

// из слага получаем имя, например, jack-torrance-ru -> Jack Torrance
function slugToName(slug?: string) {
  if (!slug) return '';
  return slug
    .split('-')
    .filter((part) => !locales.some((loc) => part === loc))
    .map(capitalizeFirst)
    .join(' ');
}
